import React from "react";
import { Button } from "@whatslly/ui-components/v2/Button";
import { useTranslation } from "react-i18next";
import styles from "./footer.module.scss";
import { TFunction } from "i18next";
import { Text } from "@whatslly/ui-components/Text";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import { FaFileCsv, FaFilePdf } from "react-icons/fa";
import { ActionsMenu } from "@whatslly/ui-components/ActionsMenu";

export const Footer: React.FC<{
  mode: "download" | "delete";
  closeDeleteMode: VoidFunction;
  clickOnDeleteMessages?: VoidFunction;
  numOfSelectedMessages: number;
  selectAll?: VoidFunction;
  downloadMessages?: (format: "csv" | "pdf") => any;
}> = ({ closeDeleteMode, numOfSelectedMessages, selectAll, downloadMessages, mode, clickOnDeleteMessages }) => {
  const { t } = useTranslation();

  const svgTestId = `${mode}-message-close`,
    textTestId = `${mode}-message-text`,
    buttonTestId = `${mode}-message-button`,
    buttonText = t(`chat_component.${mode}_messages`);

  return (
    <div className={styles.footer}>
      <div className={styles.closeDeleteContainer}>
        <SvgV2 data-testid={svgTestId} width={12} height={12} iconName="close" onClick={closeDeleteMode} className={styles.closeIcon} />
        <Selected numOfSelectedMessages={numOfSelectedMessages} textTestId={textTestId} t={t} />
        <SelectAll t={t} numOfSelectedMessages={numOfSelectedMessages} selectAll={selectAll} />
      </div>
      {mode === "delete" ? (
        <DeleteButton buttonText={buttonText} buttonTestId={buttonTestId} clickOnDeleteMessages={clickOnDeleteMessages} />
      ) : (
        <DownloadButton
          buttonTestId={buttonTestId}
          t={t}
          numOfSelectedMessages={numOfSelectedMessages}
          buttonText={buttonText}
          downloadMessages={downloadMessages}
        />
      )}
    </div>
  );
};

const SelectAll = ({ numOfSelectedMessages, selectAll, t }: { numOfSelectedMessages: number; selectAll?: VoidFunction; t: TFunction }) => {
  return (
    <div className={styles.selectAll} onClick={selectAll}>
      <Button testId={"select-all-msgs"} height={"small"} fontSize={"small"} paddingInline={"small"}>
        {numOfSelectedMessages > 0 ? t("chat_component.deselect_all") : t("chat_component.select_all")}
      </Button>
    </div>
  );
};

const Selected = ({ textTestId, numOfSelectedMessages, t }: { textTestId: string; numOfSelectedMessages: number; t: TFunction }) => {
  return (
    <div data-testid={textTestId} className={styles.closeDeleteContainer}>
      <Text weight={"normal"}>{`${numOfSelectedMessages} ${t("chat_component.selected")}`}</Text>
    </div>
  );
};

const DeleteButton = ({
  buttonTestId,
  clickOnDeleteMessages,
  buttonText,
}: {
  buttonTestId: string;
  buttonText: string;
  clickOnDeleteMessages?: VoidFunction;
}) => {
  return (
    <Button onClick={clickOnDeleteMessages} testId={buttonTestId} height={"small"} fontSize={"small"} paddingInline={"small"}>
      {buttonText}
    </Button>
  );
};

const DownloadButton = ({
  numOfSelectedMessages,
  buttonTestId,
  downloadMessages,
  buttonText,
  t,
}: {
  numOfSelectedMessages: number;
  buttonTestId: string;
  buttonText: string;
  downloadMessages?: (format: "csv" | "pdf") => any;
  t: TFunction;
}) => {
  return (
    <ActionsMenu
      className={styles.download}
      disabled={numOfSelectedMessages === 0}
      icon={
        <Button
          height={"small"}
          paddingInline={"small"}
          className={styles.download}
          disabled={numOfSelectedMessages === 0}
          testId={buttonTestId}
          text={buttonText}
        />
      }
      position={"top-left"}
      actions={[
        {
          children: t("chat_component.export_pdf"),
          onClick: () => downloadMessages && downloadMessages("pdf"),
          icon: <FaFilePdf />,
          testId: "export-pdf-format",
        },
        {
          children: t("chat_component.export_csv"),
          onClick: () => downloadMessages && downloadMessages("csv"),
          icon: <FaFileCsv />,
          testId: "export-csv-format",
        },
      ]}
    />
  );
};
